import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Flame, Trophy, Target, Award, BookOpen, Headphones, Heart, Compass, GraduationCap, BarChart3 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Progress } from './ui/progress';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  Flame: Flame,
  Trophy: Trophy,
  Target: Target,
  Award: Award,
  BookOpen: BookOpen,
  Headphones: Headphones,
  Heart: Heart,
  Compass: Compass,
  GraduationCap: GraduationCap,
};

export const StreakDisplay = ({ streak, onClick }) => {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-500 text-white px-4 py-2 rounded-full shadow-lg hover:shadow-xl transition-all"
      data-testid="streak-display"
    >
      <Flame className="w-5 h-5 animate-pulse" />
      <span className="font-bold">{streak}</span>
    </button>
  );
};

export const GamificationWidget = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBadges, setShowBadges] = useState(false);
  const [showCheckin, setShowCheckin] = useState(false);
  const [checkinMood, setCheckinMood] = useState(3);
  const [checkinEnergy, setCheckinEnergy] = useState(3);

  const fetchStatus = async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API_URL}/gamification/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(res.data);
      
      // Afficher les nouveaux badges
      if (res.data.badges?.new_badges?.length > 0) {
        res.data.badges.new_badges.forEach(badge => {
          toast.success(`🏅 Nouveau badge : ${badge.name}!`, {
            description: badge.description,
            duration: 5000
          });
        });
      }
      
      // Proposer le check-in si pas fait
      if (!res.data.checkin?.done_today) {
        // On peut proposer le check-in plus tard
      }
    } catch (error) {
      console.error('Erreur gamification:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, [token]);

  const submitCheckin = async () => {
    try {
      const res = await axios.post(
        `${API_URL}/gamification/checkin?mood=${checkinMood}&energy=${checkinEnergy}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.new_badges?.length > 0) {
        res.data.new_badges.forEach(badge => {
          toast.success(`🏅 Nouveau badge : ${badge.name}!`, {
            description: badge.description,
            duration: 5000
          });
        });
      }
      
      toast.success('Check-in enregistré !');
      setShowCheckin(false);
      fetchStatus();
    } catch (error) {
      toast.error('Erreur lors du check-in');
    }
  };

  if (loading || !status) return null;

  const moodEmojis = ['😢', '😕', '😐', '🙂', '😄'];
  const energyEmojis = ['😴', '🥱', '😌', '💪', '⚡'];

  return (
    <>
      {/* Widget principal - Simplifié */}
      <div 
        className="bg-white rounded-2xl border border-sand-200 p-4 shadow-sm"
        data-testid="gamification-widget"
      >
        {/* Progression claire : une seule métrique */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-sage-600">Séance {status.progress.themes_listened}/11</span>
          </div>
          <div className="flex items-center gap-2">
            {status.streak.current > 0 && (
              <div className="flex items-center gap-1 bg-orange-50 px-2.5 py-1 rounded-full">
                <Flame className="w-3.5 h-3.5 text-orange-500" />
                <span className="text-xs font-bold text-orange-600">{status.streak.current}j</span>
              </div>
            )}
            <button
              onClick={() => setShowBadges(true)}
              className="flex items-center gap-1 bg-sage-100 hover:bg-sage-200 px-2.5 py-1 rounded-full transition-colors"
              data-testid="show-badges-btn"
            >
              <Trophy className="w-3.5 h-3.5 text-amber-500" />
              <span className="text-xs font-medium text-sage-700">{status.badges.earned_count}/{status.badges.total_count}</span>
            </button>
          </div>
        </div>

        <Progress value={status.progress.percent} className="h-2" />
      </div>

      {/* Modal Badges */}
      <Dialog open={showBadges} onOpenChange={setShowBadges}>
        <DialogContent className="max-w-md mx-4 bg-white rounded-2xl max-h-[80vh] overflow-hidden" data-testid="badges-modal">
          <DialogHeader className="pb-2">
            <DialogTitle className="text-xl font-serif font-light text-sage-700 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-500" />
              Mes badges
            </DialogTitle>
          </DialogHeader>
          
          <div className="overflow-y-auto max-h-[60vh] py-2">
            <div className="grid grid-cols-3 gap-3">
              {status.badges.all.map((badge) => {
                const Icon = iconMap[badge.icon] || Award;
                return (
                  <div
                    key={badge.id}
                    className={`p-3 rounded-xl text-center transition-all ${
                      badge.earned
                        ? 'bg-gradient-to-br from-amber-50 to-amber-100 border-2 border-amber-300'
                        : 'bg-gray-100 opacity-50 border-2 border-transparent'
                    }`}
                    data-testid={`badge-${badge.id}`}
                  >
                    <div className={`w-10 h-10 mx-auto mb-2 rounded-full flex items-center justify-center ${
                      badge.earned ? 'bg-amber-200' : 'bg-gray-200'
                    }`}>
                      <Icon className={`w-5 h-5 ${badge.earned ? 'text-amber-600' : 'text-gray-400'}`} />
                    </div>
                    <p className={`text-xs font-medium ${badge.earned ? 'text-sage-700' : 'text-gray-400'}`}>
                      {badge.name}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Check-in */}
      <Dialog open={showCheckin} onOpenChange={setShowCheckin}>
        <DialogContent className="max-w-sm mx-4 bg-white rounded-2xl" data-testid="checkin-modal">
          <DialogHeader className="pb-2 text-center">
            <DialogTitle className="text-xl font-serif font-light text-sage-700">
              Comment allez-vous ?
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Humeur */}
            <div>
              <p className="text-sm text-sage-600 mb-3 text-center">Votre humeur</p>
              <div className="flex justify-center gap-2">
                {moodEmojis.map((emoji, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCheckinMood(idx + 1)}
                    className={`w-12 h-12 text-2xl rounded-xl transition-all ${
                      checkinMood === idx + 1
                        ? 'bg-sage-200 scale-110 shadow-md'
                        : 'bg-sage-50 hover:bg-sage-100'
                    }`}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>

            {/* Énergie */}
            <div>
              <p className="text-sm text-sage-600 mb-3 text-center">Votre énergie</p>
              <div className="flex justify-center gap-2">
                {energyEmojis.map((emoji, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCheckinEnergy(idx + 1)}
                    className={`w-12 h-12 text-2xl rounded-xl transition-all ${
                      checkinEnergy === idx + 1
                        ? 'bg-amber-200 scale-110 shadow-md'
                        : 'bg-amber-50 hover:bg-amber-100'
                    }`}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={submitCheckin}
              className="w-full bg-sage-600 hover:bg-sage-600 text-white py-3 rounded-xl font-medium transition-colors"
              data-testid="submit-checkin-btn"
            >
              Enregistrer
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GamificationWidget;
